import { type SupportedLocale } from '@zep-us/i18n';
import { Trans, useTranslation } from 'next-i18next';
import { NoSSR } from '@/components';
import { useLocale, useViewport } from '@/hooks';
import { useCountryCode } from '@/shared/hooks';
import { FunctionCard, type FunctionCardProps } from './FunctionCard';
export const PricingSection2 = () => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  return <section className="flex flex-col items-center gap-xl px-md py-2xl mobile-lg:px-lg tablet-lg:px-2xl tablet-lg:py-3xl" data-sentry-component="PricingSection2" data-sentry-source-file="PricingSection2.tsx">
      <h2 className="whitespace-pre-line text-center text-display-sm-extrabold">
        <Trans t={t} i18nKey="pricing:functionCards.title" components={{
        Gradient: <strong className="bg-clip-text text-transparent bg-gradient-violet-blue" />
      }} data-sentry-element="Trans" data-sentry-source-file="PricingSection2.tsx" />
      </h2>
      <div className="grid w-full max-w-[1280px] grid-cols-1 gap-md tablet-lg:grid-cols-2 laptop-sm:grid-cols-6 laptop-sm:gap-lg">
        <FunctionCards data-sentry-element="FunctionCards" data-sentry-source-file="PricingSection2.tsx" />
        <MoreCard data-sentry-element="MoreCard" data-sentry-source-file="PricingSection2.tsx" />
      </div>
    </section>;
};
export const FunctionCards = () => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  const {
    locale
  } = useLocale();
  const {
    countryCode
  } = useCountryCode();
  const {
    isPortable,
    isLaptopSm
  } = useViewport();
  const getImagePath = (filename: string) => {
    const AVAILABLE_LOCALES: SupportedLocale[] = ['ko', 'en', 'ja'];
    const localeKey = AVAILABLE_LOCALES.includes(locale) ? locale : 'en';
    return `/assets/pricing/${filename.replace('{locale}', localeKey)}`;
  };
  const CARDS_DATA: FunctionCardProps[] = [{
    id: 'premium-maps',
    layout: isPortable ? 'vertical' : 'horizontal',
    className: 'col-span-1 tablet-lg:col-span-2 laptop-sm:col-span-6 bg-green-50',
    title: t('pricing:premiumMaps.title'),
    description: t('pricing:premiumMaps.description'),
    imgSrc: 'function_premium_maps_language_all.webp'
  }, {
    id: 'quiz-ai',
    layout: isPortable || isLaptopSm ? 'vertical' : 'horizontal',
    className: 'col-span-1 laptop-sm:col-span-6 bg-blue-50',
    title: t('pricing:quizAi.title'),
    description: t('pricing:quizAi.description'),
    imgSrc: 'function_quiz_ai_language_{locale}.webp'
  }, {
    id: 'learning-report',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-3 bg-red-50',
    title: t('pricing:learningReport.title'),
    description: t('pricing:learningReport.description'),
    imgSrc: 'function_learning_report_language_{locale}.webp'
  }, {
    id: 'ai-tutor',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-3 bg-yellow-50',
    title: t('pricing:aiTutor.title'),
    description: t('pricing:aiTutor.description'),
    imgSrc: 'function_ai_tutor_language_all.webp',
    visibleCountries: ['KR']
  }, {
    id: 'ai-translation',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-3 bg-yellow-50',
    title: t('pricing:aiTranslation.title'),
    description: t('pricing:aiTranslation.description'),
    imgSrc: 'function_ai_translation_language_all.webp',
    hideCountries: ['KR']
  }, {
    id: 'lock',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-2 bg-neutral',
    title: t('pricing:quizLock.title'),
    description: t('pricing:quizLock.description'),
    imgSrc: 'function_lock_language_{locale}.webp'
  }, {
    id: 'worksheet',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-2 bg-neutral',
    title: t('pricing:worksheet.title'),
    description: t('pricing:worksheet.description'),
    imgSrc: 'function_worksheet_language_{locale}.webp'
  }, {
    id: 'next-quiz',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-2 bg-neutral',
    title: t('pricing:nextQuiz.title'),
    description: t('pricing:nextQuiz.description'),
    imgSrc: 'function_next_quiz_language_all.webp'
  }];
  const cards = CARDS_DATA.filter(card => {
    if (card.visibleCountries) {
      return card.visibleCountries.includes(countryCode);
    }
    if (card.hideCountries) {
      return !card.hideCountries.includes(countryCode);
    }
    return true;
  });
  return <NoSSR data-sentry-element="NoSSR" data-sentry-component="FunctionCards" data-sentry-source-file="PricingSection2.tsx">
      {cards.map(card => card.layout === 'horizontal' ? <FunctionCard.Horizontal key={card.id} className={card.className} title={card.title} description={card.description} imgSrc={getImagePath(card.imgSrc)} /> : <FunctionCard.Vertical key={card.id} className={card.className} title={card.title} description={card.description} imgSrc={getImagePath(card.imgSrc)} />)}
    </NoSSR>;
};
const MoreCard = () => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  return <article className="col-span-1 flex flex-col items-center gap-md rounded-lg border border-alternative bg-neutral p-2xl tablet-lg:col-span-2 laptop-sm:col-span-6" data-sentry-component="MoreCard" data-sentry-source-file="PricingSection2.tsx">
      <h5 className="text-heading-xl-extrabold text-alternative">
        {t('pricing:home.pricing.moreFunctionComing')}
      </h5>
      <p className="text-body-lg-semibold text-alternative">
        {t('pricing:moreComingSoon')}
      </p>
    </article>;
};